/**
 * ChatbotAnalytics - Main Parent Component
 * 
 * This component serves as the main container for the Chatbot Analytics module.
 * It provides a tabbed interface to navigate between different analytics views.
 */

import React, { useState, lazy, Suspense } from 'react';
import FusePageSimple from '@fuse/core/FusePageSimple';
import FuseAnimate from '@fuse/core/FuseAnimate/FuseAnimate';
import FuseLoading from '@fuse/core/FuseLoading';
import PageHeading from 'allGlobalComponents/PageHeading';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimelineIcon from '@mui/icons-material/Timeline';
import PeopleIcon from '@mui/icons-material/People';
import RouteIcon from '@mui/icons-material/Route';

// Lazy load tab components for better performance
const PeakDaysTab = lazy(() => import('./tabs/PeakDaysTab'));
const PeakHoursTab = lazy(() => import('./tabs/PeakHoursTab'));
const InteractionFrequencyTab = lazy(() => import('./tabs/InteractionFrequencyTab'));
const EngagedTab = lazy(() => import('./tabs/EngagedTab'));
const CustomerJourneyTab = lazy(() => import('./tabs/CustomerJourneyTab'));

const useStyles = makeStyles(theme => ({
	root: {
		width: '100%'
	},
	headerBar: {
		backgroundColor: 'transparent !important',
		padding: '0 16px'
	},
	headerContainer: {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		padding: theme.spacing(0, 2)
	},
	headerIcon: {
		marginRight: theme.spacing(1.5)
	},
	headerTitle: {
		fontSize: '18px',
		fontWeight: 600,
		color: theme.palette.text.primary
	},
	contentContainer: {
		padding: theme.spacing(2)
	},
	tabsContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: 0,
		backgroundColor: theme.palette.type === 'dark' ? '#1F2937' : '#F3F4F6',
		borderRadius: 12,
		padding: 4,
		marginBottom: theme.spacing(3),
		width: 'fit-content',
		flexWrap: 'wrap'
	},
	tab: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		padding: theme.spacing(1.25, 3),
		borderRadius: 10,
		cursor: 'pointer',
		fontSize: 14,
		fontWeight: 500,
		color: theme.palette.text.secondary,
		backgroundColor: 'transparent',
		border: 'none',
		transition: 'all 0.2s ease',
		'&:hover': {
			backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'
		}
	},
	activeTab: {
		backgroundColor: theme.palette.type === 'dark' ? '#374151' : '#FFFFFF',
		color: theme.palette.type === 'dark' ? '#FFFFFF' : theme.palette.primary.main,
		boxShadow: theme.palette.type === 'dark'
			? '0 2px 8px rgba(0,0,0,0.3)'
			: '0 2px 8px rgba(0,0,0,0.08)',
		fontWeight: 600
	},
	tabIcon: {
		fontSize: 20
	},
	tabContent: {
		minHeight: 400
	},
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: 400
	}
}));
/**
 * Tab configuration
 */

function ChatbotAnalytics({ data = null }) {
	let agentPermissions = JSON.parse(localStorage.getItem('user_acl'));
	const TABS = [
		{ id: 'customer-journey', label: 'Customer Journey', icon: RouteIcon, component: CustomerJourneyTab, permission: 'FRONT:/chatbot/journey' },
		{ id: 'peak-days', label: 'Peak Days', icon: CalendarTodayIcon, component: PeakDaysTab },
		{ id: 'peak-hours', label: 'Peak Hours', icon: AccessTimeIcon, component: PeakHoursTab },
		{ id: 'interaction-frequency', label: 'Interaction Frequency', icon: TimelineIcon, component: InteractionFrequencyTab },
		{ id: 'engaged', label: 'Engaged', icon: PeopleIcon, component: EngagedTab },
	].filter(tab => !tab.permission || (agentPermissions && agentPermissions[tab.permission]));

	const classes = useStyles();
	const theme = useTheme();

	// State for tracking active tab
	const [activeTab, setActiveTab] = useState(TABS[0]?.id || 'peak-days');

	// Render active tab content
	const renderTabContent = () => {
		const currentTab = TABS.find(tab => tab.id === activeTab);
		if (!currentTab) return null;

		const TabComponent = currentTab.component;
		return (
			<Suspense
				fallback={
					<div className={classes.loadingContainer}>
						<FuseLoading />
					</div>
				}
			>
				<TabComponent userData={data} />
			</Suspense>
		);
	};

	return (
		<FusePageSimple
			classes={{ header: classes.headerBar }}
			header={<PageHeading icon={AnalyticsIcon} title="Chatbot Analytics" />}
			content={
				<div className={classes.contentContainer}>
					{/* Tabs */}
					<div className={classes.tabsContainer}>
						{TABS.map((tab) => {
							const IconComponent = tab.icon;
							return (
								<button
									key={tab.id}
									className={`${classes.tab} ${activeTab === tab.id ? classes.activeTab : ''}`}
									onClick={() => setActiveTab(tab.id)}
								>
									<IconComponent className={classes.tabIcon} />
									{tab.label}
								</button>
							);
						})}
					</div>

					{/* Tab Content */}
					<div className={classes.tabContent}>
						{renderTabContent()}
					</div>
				</div>
			}
		/>
	);
}

export default ChatbotAnalytics;